import React, { useState, useCallback } from 'react';
import {
    Plus,
    GripVertical,
    Pencil,
    Trash2,
    Check,
    X,
    Tag,
    Hash,
    Calendar,
    ListChecks,
    CheckSquare,
    Link,
    Mail,
    User,
    Type,
    Flag,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    useCustomFields,
    useCreateCustomField,
    useUpdateCustomField,
    useDeleteCustomField,
} from '@/hooks/use-custom-fields';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import type { CustomField, CustomFieldType, CustomFieldOption, CreateCustomFieldInput } from '@/lib/schemas';

// ============================================
// SPRINT SHAPE
// ============================================
//
// SPRINT custom fields store their payload as `{ sprints: SprintDef[] }`
// in the JSON column (mirrors `sprintDefSchema` in
// backend/src/schemas/custom-field.ts). We keep a parallel shape here so
// the option editor can edit name + start + end + colour per sprint.

export interface SprintDef {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    color?: string;
}

export interface SprintOptionsPayload {
    sprints: SprintDef[];
}

/**
 * Parse whatever the backend wrote into CustomField.options into the
 * SprintDef[] shape. Handles:
 *  - the structured `{ sprints: [...] }` payload (canonical)
 *  - a legacy flat CustomFieldOption[] (treated as empty)
 *  - malformed shapes (returns [])
 */
export function parseSprintOptions(raw: unknown): SprintDef[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return [];
    if (typeof raw !== 'object') return [];
    const obj = raw as Record<string, unknown>;
    const arr = obj.sprints;
    if (!Array.isArray(arr)) return [];
    return arr
        .map((entry): SprintDef | null => {
            if (!entry || typeof entry !== 'object') return null;
            const e = entry as Record<string, unknown>;
            if (typeof e.id !== 'string') return null;
            if (typeof e.name !== 'string') return null;
            if (typeof e.startDate !== 'string') return null;
            if (typeof e.endDate !== 'string') return null;
            const color = typeof e.color === 'string' ? e.color : undefined;
            return {
                id: e.id,
                name: e.name,
                startDate: e.startDate,
                endDate: e.endDate,
                color,
            };
        })
        .filter((s): s is SprintDef => s !== null);
}

// ============================================
// FIELD TYPE CONFIG
// ============================================

const fieldTypeConfig: Record<CustomFieldType, { labelKey: keyof ReturnType<typeof import('@/lib/i18n').useI18n>['t'] extends (key: infer K) => string ? K : never; icon: React.ElementType; color: string }> = {
    TEXT: { labelKey: 'customFields.text', icon: Type, color: 'text-gray-500' },
    NUMBER: { labelKey: 'customFields.number', icon: Hash, color: 'text-blue-500' },
    DATE: { labelKey: 'customFields.date', icon: Calendar, color: 'text-green-500' },
    SELECT: { labelKey: 'customFields.select', icon: ListChecks, color: 'text-purple-500' },
    MULTI_SELECT: { labelKey: 'customFields.multiSelect', icon: Tag, color: 'text-orange-500' },
    CHECKBOX: { labelKey: 'customFields.checkbox', icon: CheckSquare, color: 'text-cyan-500' },
    URL: { labelKey: 'customFields.url', icon: Link, color: 'text-indigo-500' },
    EMAIL: { labelKey: 'customFields.email', icon: Mail, color: 'text-pink-500' },
    PERSON: { labelKey: 'customFields.person', icon: User, color: 'text-amber-500' },
    // t16: SPRINT field. Stores { sprints: [{id, name, startDate, endDate, color?}] }
    // in CustomField.options; the Calendar view reads this to render sprint bands.
    SPRINT: { labelKey: 'customFields.sprint', icon: Flag, color: 'text-rose-500' },
};

const colorOptions = [
    '#ef4444', // red
    '#f97316', // orange
    '#f59e0b', // amber
    '#84cc16', // lime
    '#22c55e', // green
    '#14b8a6', // teal
    '#06b6d4', // cyan
    '#3b82f6', // blue
    '#6366f1', // indigo
    '#8b5cf6', // violet
    '#a855f7', // purple
    '#ec4899', // pink
];

// ============================================
// OPTION EDITOR
// ============================================

interface OptionEditorProps {
    options: CustomFieldOption[];
    onChange: (options: CustomFieldOption[]) => void;
}

function OptionEditor({ options, onChange }: OptionEditorProps) {
    const { t } = useI18n();
    const [newLabel, setNewLabel] = useState('');

    const addOption = () => {
        if (!newLabel.trim()) return;
        const newOption: CustomFieldOption = {
            id: `opt_${Date.now()}`,
            label: newLabel.trim(),
            color: colorOptions[options.length % colorOptions.length],
        };
        onChange([...options, newOption]);
        setNewLabel('');
    };

    const removeOption = (id: string) => {
        onChange(options.filter((opt) => opt.id !== id));
    };

    const updateOption = (id: string, updates: Partial<CustomFieldOption>) => {
        onChange(options.map((opt) => (opt.id === id ? { ...opt, ...updates } : opt)));
    };

    return (
        <div className="space-y-2">
            <Label>{t('customFields.options')}</Label>
            <div className="space-y-2">
                {options.map((option) => (
                    <div key={option.id} className="flex items-center gap-2">
                        <div className="relative">
                            <input
                                type="color"
                                value={option.color || '#6366f1'}
                                onChange={(e) => updateOption(option.id, { color: e.target.value })}
                                className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                            />
                        </div>
                        <Input
                            value={option.label}
                            onChange={(e) => updateOption(option.id, { label: e.target.value })}
                            className="flex-1 h-8"
                        />
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => removeOption(option.id)}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>
            <div className="flex items-center gap-2">
                <Input
                    placeholder={t('customFields.addOption')}
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                    className="flex-1 h-8"
                />
                <Button variant="outline" size="sm" onClick={addOption} disabled={!newLabel.trim()}>
                    <Plus className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}

// ============================================
// SPRINT OPTION EDITOR
// ============================================
//
// Renders one row per sprint with: name input, start date input,
// end date input, colour picker, remove button. The parent persists
// the resulting `{ sprints: [...] }` payload via PATCH
// /api/projects/:projectId/custom-fields/:fieldId.

interface SprintOptionEditorProps {
    sprints: SprintDef[];
    onChange: (sprints: SprintDef[]) => void;
}

function SprintOptionEditor({ sprints, onChange }: SprintOptionEditorProps) {
    const { t } = useI18n();

    const addSprint = () => {
        const newSprint: SprintDef = {
            id: `sprint_${Date.now()}`,
            name: '',
            startDate: '',
            endDate: '',
            color: colorOptions[sprints.length % colorOptions.length],
        };
        onChange([...sprints, newSprint]);
    };

    const removeSprint = (id: string) => {
        onChange(sprints.filter((s) => s.id !== id));
    };

    const updateSprint = (id: string, updates: Partial<SprintDef>) => {
        onChange(sprints.map((s) => (s.id === id ? { ...s, ...updates } : s)));
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <Label>{t('customFields.options')}</Label>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={addSprint}
                    title={t('customFields.sprintAddOption')}
                >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('customFields.sprintAddOption')}
                </Button>
            </div>
            {sprints.length === 0 ? (
                <p className="text-xs text-foreground-muted py-2">
                    {t('customFields.sprintNoOptions')}
                </p>
            ) : (
                <div className="space-y-2">
                    {sprints.map((sprint) => (
                        // R2 layout: colour picker on the left, sprint NAME
                        // on the first row, then START + END date inputs on
                        // a second row. Was a single flex-wrap row before;
                        // the dates are now visually grouped with the name.
                        <div
                            key={sprint.id}
                            className="flex items-start gap-2 rounded-md border border-white/10 p-2"
                        >
                            <input
                                type="color"
                                value={sprint.color || '#6366f1'}
                                onChange={(e) => updateSprint(sprint.id, { color: e.target.value })}
                                className="w-8 h-8 rounded cursor-pointer border-0 p-0 shrink-0 mt-1"
                                aria-label={t('customFields.sprintOptionName')}
                                title={t('customFields.sprintOptionName')}
                            />
                            <div className="flex-1 min-w-0 space-y-1.5">
                                <Input
                                    value={sprint.name}
                                    onChange={(e) => updateSprint(sprint.id, { name: e.target.value })}
                                    placeholder={t('customFields.sprintOptionName')}
                                    className="h-8"
                                    aria-label={t('customFields.sprintOptionName')}
                                />
                                <div className="flex flex-wrap items-center gap-1">
                                    <Input
                                        type="date"
                                        value={sprint.startDate || ''}
                                        onChange={(e) => updateSprint(sprint.id, { startDate: e.target.value })}
                                        className="h-8 flex-1 min-w-[140px]"
                                        aria-label={t('customFields.sprintOptionStart')}
                                        title={t('customFields.sprintOptionStart')}
                                    />
                                    <Input
                                        type="date"
                                        value={sprint.endDate || ''}
                                        onChange={(e) => updateSprint(sprint.id, { endDate: e.target.value })}
                                        className="h-8 flex-1 min-w-[140px]"
                                        aria-label={t('customFields.sprintOptionEnd')}
                                        title={t('customFields.sprintOptionEnd')}
                                    />
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0 mt-1"
                                onClick={() => removeSprint(sprint.id)}
                                title={t('common.delete')}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ============================================
// FIELD FORM DIALOG
// ============================================

export interface FieldFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    projectId: string;
    editingField?: CustomField | null;
}

export function FieldFormDialog({ open, onOpenChange, projectId, editingField }: FieldFormDialogProps) {
    const { t } = useI18n();
    const createField = useCreateCustomField(projectId);
    const updateField = useUpdateCustomField(projectId);
    const isEditing = !!editingField;
    const isLoading = createField.isPending || updateField.isPending;

    const [name, setName] = useState(editingField?.name || '');
    const [type, setType] = useState<CustomFieldType>(editingField?.type || 'TEXT');
    // SELECT / MULTI_SELECT use the flat CustomFieldOption[] shape.
    const [options, setOptions] = useState<CustomFieldOption[]>(editingField?.options || []);
    // SPRINT uses the structured `{ sprints: SprintDef[] }` shape that
    // the backend persists in the JSON column. We keep separate state
    // and only swap to/from it when the type toggles to SPRINT.
    const [sprints, setSprints] = useState<SprintDef[]>(
        editingField?.type === 'SPRINT' ? parseSprintOptions(editingField.options) : []
    );

    // Reset form when dialog opens or editingField changes
    React.useEffect(() => {
        if (open) {
            setName(editingField?.name || '');
            setType(editingField?.type || 'TEXT');
            setOptions(editingField?.options || []);
            setSprints(
                editingField?.type === 'SPRINT' ? parseSprintOptions(editingField.options) : []
            );
        }
    }, [open, editingField]);

    // Clear options when switching away from SELECT/MULTI_SELECT.
    React.useEffect(() => {
        if (type !== 'SELECT' && type !== 'MULTI_SELECT') {
            setOptions([]);
        }
        if (type !== 'SPRINT') {
            setSprints([]);
        }
    }, [type]);

    const needsOptions = type === 'SELECT' || type === 'MULTI_SELECT' || type === 'SPRINT';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            // Decide which payload shape to send: SELECT/MULTI_SELECT
            // use a flat array, SPRINT uses the structured `{ sprints: [...] }`
            // shape (backend accepts both via customFieldOptionsUnionSchema).
            let payloadOptions: unknown = undefined;
            if (type === 'SPRINT') {
                payloadOptions = { sprints };
            } else if (type === 'SELECT' || type === 'MULTI_SELECT') {
                payloadOptions = options;
            }

            if (isEditing && editingField) {
                await updateField.mutateAsync({
                    fieldId: editingField.id,
                    data: { name, options: payloadOptions as any },
                });
            } else {
                await createField.mutateAsync({
                    name,
                    type,
                    options: payloadOptions as any,
                });
            }
            onOpenChange(false);
            setName('');
            setType('TEXT');
            setOptions([]);
            setSprints([]);
        } catch (error) {
            console.error('Error saving field:', error);
        }
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (newOpen) {
            setName(editingField?.name || '');
            setType(editingField?.type || 'TEXT');
            setOptions(editingField?.options || []);
            setSprints(
                editingField?.type === 'SPRINT' ? parseSprintOptions(editingField.options) : []
            );
        }
        onOpenChange(newOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-h-[90dvh] overflow-hidden p-0">
                <form onSubmit={handleSubmit} className="flex min-h-0 max-h-[90dvh] flex-col">
                    <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
                        <DialogTitle>{isEditing ? t('customFields.editFieldTitle') : t('customFields.addFieldTitle')}</DialogTitle>
                        <DialogDescription>
                            {isEditing
                                ? t('customFields.editFieldDescription')
                                : t('customFields.addFieldDescription')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain space-y-4 px-6 py-3">
                        <div className="space-y-2">
                            <Label htmlFor="name">{t('customFields.fieldName')}</Label>
                            <Input
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t('customFields.fieldNamePlaceholder')}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>{t('customFields.fieldType')}</Label>
                            <Select
                                value={type}
                                onValueChange={(v) => setType(v as CustomFieldType)}
                                disabled={isEditing}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(fieldTypeConfig).map(([key, config]) => {
                                        const Icon = config.icon;
                                        return (
                                            <SelectItem key={key} value={key}>
                                                <div className="flex items-center gap-2">
                                                    <Icon className={cn('h-4 w-4', config.color)} />
                                                    {t(config.labelKey as any)}
                                                </div>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>

                        {needsOptions && type === 'SPRINT' && (
                            <SprintOptionEditor sprints={sprints} onChange={setSprints} />
                        )}
                        {needsOptions && type !== 'SPRINT' && (
                            <OptionEditor options={options} onChange={setOptions} />
                        )}
                    </div>

                    <DialogFooter className="shrink-0 border-t border-white/10 px-6 py-4">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button type="submit" disabled={isLoading || !name.trim()}>
                            {isLoading ? t('customFields.saving') : isEditing ? t('common.saveChanges') : t('customFields.createField')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ============================================
// CUSTOM FIELD MANAGER
// ============================================

interface CustomFieldManagerProps {
    projectId: string;
    canEdit?: boolean;
}

export function CustomFieldManager({ projectId, canEdit = true }: CustomFieldManagerProps) {
    const { t } = useI18n();
    const { data: fields = [], isLoading } = useCustomFields(projectId);
    const deleteField = useDeleteCustomField(projectId);

    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [editingField, setEditingField] = useState<CustomField | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [fieldToDelete, setFieldToDelete] = useState<CustomField | null>(null);

    const handleAddField = () => {
        setEditingField(null);
        setFormDialogOpen(true);
    };

    const handleEditField = (field: CustomField) => {
        setEditingField(field);
        setFormDialogOpen(true);
    };

    const handleDeleteClick = (field: CustomField) => {
        setFieldToDelete(field);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!fieldToDelete) return;
        try {
            await deleteField.mutateAsync(fieldToDelete.id);
            setDeleteDialogOpen(false);
            setFieldToDelete(null);
        } catch (error) {
            console.error('Error deleting field:', error);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground">{t('customFields.title')}</h3>
                {canEdit && (
                    <Button variant="outline" size="sm" onClick={handleAddField}>
                        <Plus className="h-4 w-4 mr-1" />
                        {t('customFields.addField')}
                    </Button>
                )}
            </div>

            {isLoading ? (
                <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-10 bg-surface-hover rounded animate-pulse" />
                    ))}
                </div>
            ) : fields.length === 0 ? (
                <p className="text-sm text-foreground-muted text-center py-4">
                    {t('customFields.noFieldsYet')}{canEdit && ` ${t('customFields.noFieldsHint')}`}
                </p>
            ) : (
                <div className="space-y-2">
                    {fields.map((field) => {
                        const config = fieldTypeConfig[field.type];
                        const Icon = config.icon;
                        return (
                            <div
                                key={field.id}
                                className="flex items-center gap-3 p-2 rounded-lg border border-border bg-surface hover:bg-surface-hover group"
                            >
                                {canEdit && (
                                    <GripVertical className="h-4 w-4 text-foreground-muted cursor-grab" />
                                )}
                                <Icon className={cn('h-4 w-4', config.color)} />
                                <span className="flex-1 text-sm font-medium text-foreground">
                                    {field.name}
                                </span>
                                <Badge variant="secondary" className="text-xs">
                                    {t(config.labelKey as any)}
                                </Badge>
                                {(field.type === 'SELECT' || field.type === 'MULTI_SELECT') && (
                                    <span className="text-xs text-foreground-muted">
                                        {field.options.length} {t('customFields.options').toLowerCase()}
                                    </span>
                                )}
                                {field.type === 'SPRINT' && (
                                    <span className="text-xs text-foreground-muted">
                                        {parseSprintOptions(field.options).length} {t('customFields.options').toLowerCase()}
                                    </span>
                                )}
                                {canEdit && (
                                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => handleEditField(field)}
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-destructive hover:text-destructive"
                                            onClick={() => handleDeleteClick(field)}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Field form dialog */}
            <FieldFormDialog
                open={formDialogOpen}
                onOpenChange={setFormDialogOpen}
                projectId={projectId}
                editingField={editingField}
            />

            {/* Delete confirmation dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('customFields.deleteFieldTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('customFields.deleteFieldDescription').replace('{name}', fieldToDelete?.name || '')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={deleteField.isPending}
                        >
                            {deleteField.isPending ? t('customFields.deleting') : t('common.delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
